import { Redo, Ruler, ShoppingCart, Undo } from '@carbon/icons-react';
import { SxProps, useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Box } from '@mui/material';
import Divider from '@mui/material/Divider';
import * as Preact from 'preact';
import * as Button from './A_Button';
import * as IconButton from './A_IconButton';
import * as Switch from './A_Switch';
import { isSmallScreen } from './domain/theme/breakpoints';

export interface Props {
    showDimensions: boolean;
    onDimensionsToggle: () => void;
    onUndoClick?: () => void;
    onRedoClick?: () => void;
    onSwatchClick: () => void;
    activeSwatch: string;
    basketPrice: string;
    onBasketClick: () => void;
    isPieceLimitReached: boolean;
    sx?: SxProps;
}

export const Component = (p: Props) => {
    const theme = useTheme();
    const isSmall = isSmallScreen(theme);
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    width: 'fit-content',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: isSmall ? '4px' : '8px',
                    gap: isSmall ? '8px' : '12px',
                    backgroundColor: 'background.paper',
                    borderRadius: '8px',
                    position: 'absolute',
                    boxShadow: '0px 4px 80px rgba(0, 0, 0, 0.1)',
                    ...p.sx,
                }}
            >
                <Switch.Component
                    onChange={p.onDimensionsToggle}
                    checked={p.showDimensions}
                    label={<Ruler size={isSmall ? 20 : 24} />}
                    labelPlacement="start"
                    size="small"
                    sx={{
                        '& .MuiFormControlLabel-label': {
                            display: 'flex',
                        },
                        padding: '4px 0 4px 4px',
                    }}
                />
                <Divider orientation="vertical" flexItem />
                <Box gap={isSmall ? '8px' : '12px'} display="flex">
                    <IconButton.Component
                        onClick={p.onUndoClick}
                        disabled={!p.onUndoClick}
                        size="small"
                    >
                        <Undo size={isSmall ? 20 : 24} />
                    </IconButton.Component>
                    <IconButton.Component
                        onClick={p.onRedoClick}
                        disabled={!p.onRedoClick}
                        size="small"
                    >
                        <Redo size={isSmall ? 20 : 24} />
                    </IconButton.Component>
                </Box>
                <Divider orientation="vertical" flexItem />
                <IconButton.Component
                    onClick={p.onSwatchClick}
                    sx={{
                        width: isSmall ? '24px' : '25px',
                        height: isSmall ? '24px' : '25px',
                        borderRadius: '4px',
                        background: `no-repeat center url('${p.activeSwatch}')`,
                        backgroundSize: 'contain',
                        '&:hover': {
                            background: `no-repeat center url('${p.activeSwatch}')`,
                            backgroundSize: 'contain',
                        },
                    }}
                />
                <Divider orientation="vertical" flexItem />
                <Button.Component
                    startIcon={<ShoppingCart size={isSmall ? 18 : 20} />}
                    onClick={p.onBasketClick}
                    variant="text"
                    color="primary"
                    size={isSmall ? 'small' : 'medium'}
                    sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                    }}
                >
                    {p.basketPrice}
                </Button.Component>
            </Box>
            {p.isPieceLimitReached && (
                <Alert
                    sx={{
                        position: 'absolute',
                        top: '100px',
                    }}
                    severity="info"
                >
                    You've reached your seat limit (12). Please remove a seat to continue building.
                </Alert>
            )}
        </Box>
    );
};

Component.displayName = 'Toolbar';
